import React, { useState, useEffect, useCallback } from 'react';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { Alert } from '../../components/shared/Alert';
import { UserPlus, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp, Search } from 'lucide-react';

interface RegistrationRequest {
  id: number;
  email: string;
  role: string;
  company_name: string;
  dot_number: number | null;
  mc_number: string | null;
  phone: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by: number | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
}

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

const BASE_URL = import.meta.env.VITE_API_BASE_URL ? `${import.meta.env.VITE_API_BASE_URL}/api` : '/api';

const regApi = {
  list: async (status?: string) => {
    const params = status ? `?status=${status}` : '';
    const res = await fetch(`${BASE_URL}/registration/requests${params}`, { headers: getHeaders() });
    if (!res.ok) throw new Error('Failed to fetch');
    return res.json();
  },
  approve: async (id: number, notes?: string) => {
    const res = await fetch(`${BASE_URL}/registration/requests/${id}/approve`, {
      method: 'POST', headers: getHeaders(), body: JSON.stringify({ notes }),
    });
    if (!res.ok) throw new Error('Failed to approve');
    return res.json();
  },
  reject: async (id: number, notes?: string) => {
    const res = await fetch(`${BASE_URL}/registration/requests/${id}/reject`, {
      method: 'POST', headers: getHeaders(), body: JSON.stringify({ notes }),
    });
    if (!res.ok) throw new Error('Failed to reject');
    return res.json();
  },
};

export const RegistrationRequests: React.FC = () => {
  const [requests, setRequests] = useState<RegistrationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState<string>('pending');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [actionNotes, setActionNotes] = useState<Record<number, string>>({});
  const [processing, setProcessing] = useState<number | null>(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await regApi.list(filter || undefined);
      setRequests(result.data.requests);
    } catch (err) {
      setError('Failed to load registration requests');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleApprove = async (id: number) => {
    if (!confirm('Approve this registration? A user account will be created.')) return;
    setProcessing(id);
    try {
      await regApi.approve(id, actionNotes[id]);
      setSuccess('Registration approved. User account created.');
      fetchRequests();
    } catch {
      setError('Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id: number) => {
    if (!confirm('Reject this registration request?')) return;
    setProcessing(id);
    try {
      await regApi.reject(id, actionNotes[id]);
      setSuccess('Registration request rejected.');
      fetchRequests();
    } catch {
      setError('Failed to reject request');
    } finally {
      setProcessing(null);
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
        {status === 'approved' && <CheckCircle className="w-3 h-3 mr-1" />}
        {status === 'rejected' && <XCircle className="w-3 h-3 mr-1" />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <UserPlus className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Registration Requests</h1>
              <p className="text-sm text-gray-500">Review and manage user registration requests</p>
            </div>
          </div>
        </div>

        {error && <Alert type="danger" message={error} onClose={() => setError('')} />}
        {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

        <div className="flex space-x-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
          {['pending', 'approved', 'rejected', ''].map(s => (
            <button
              key={s || 'all'}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === s ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><LoadingSpinner message="Loading requests..." /></div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No {filter || ''} registration requests found.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map(req => (
              <div key={req.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedId(expandedId === req.id ? null : req.id)}
                >
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="font-medium text-gray-900">{req.email}</div>
                      <div className="text-sm text-gray-500">{req.company_name} &middot; {req.role}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {statusBadge(req.status)}
                    <span className="text-xs text-gray-400">{new Date(req.created_at).toLocaleDateString()}</span>
                    {expandedId === req.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>

                {expandedId === req.id && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                      <div><span className="text-gray-500">Role:</span> <span className="font-medium">{req.role}</span></div>
                      <div><span className="text-gray-500">Company:</span> <span className="font-medium">{req.company_name}</span></div>
                      {req.dot_number && <div><span className="text-gray-500">DOT#:</span> <span className="font-medium">{req.dot_number}</span></div>}
                      {req.mc_number && <div><span className="text-gray-500">MC#:</span> <span className="font-medium">{req.mc_number}</span></div>}
                      {req.phone && <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{req.phone}</span></div>}
                      <div><span className="text-gray-500">Submitted:</span> <span className="font-medium">{new Date(req.created_at).toLocaleString()}</span></div>
                      {req.reviewed_at && <div><span className="text-gray-500">Reviewed:</span> <span className="font-medium">{new Date(req.reviewed_at).toLocaleString()}</span></div>}
                      {req.review_notes && <div className="col-span-2"><span className="text-gray-500">Notes:</span> <span className="font-medium">{req.review_notes}</span></div>}
                    </div>

                    {req.status === 'pending' && (
                      <div className="flex items-end gap-3 mt-3">
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">Admin Notes (optional)</label>
                          <input
                            type="text"
                            value={actionNotes[req.id] || ''}
                            onChange={e => setActionNotes({ ...actionNotes, [req.id]: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                            placeholder="Add review notes..."
                          />
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleApprove(req.id); }}
                          disabled={processing === req.id}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium disabled:opacity-50 flex items-center gap-1"
                        >
                          <CheckCircle className="w-4 h-4" /> Approve
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleReject(req.id); }}
                          disabled={processing === req.id}
                          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium disabled:opacity-50 flex items-center gap-1"
                        >
                          <XCircle className="w-4 h-4" /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RegistrationRequests;
